import { useId, useState } from 'react';

import type { ColorName } from '../Input2/buttonClasses';
import { cn } from '../cn';
import { Button } from '../Input2';
import { Alerts, Arrows, General } from '../StrokeIcons';
import { CopyFeedbackButton } from './CopyFeedbackButton';

// ============================================================================
// Types
// ============================================================================

/**
 * - `failed`: the send did not happen (LinkedIn / Unipile refused it). Red.
 * - `blocked`: we refused to send on purpose — something the rep must look at first. Amber.
 * - `sentRecordPending`: the message went out; only our own activity record is behind. Green.
 */
export type SendOutcomeTone = 'failed' | 'blocked' | 'sentRecordPending';

export type SendOutcomeNoticePureProps = {
  tone: SendOutcomeTone;
  /** What happened, in the rep's words ("Not sent."). */
  title: string;
  /** What to do about it. No codes and no provider vocabulary — those live in `detail`. */
  body?: string;
  /** The technical story, shown only behind "More details" (e.g. "Unipile 429: rate_limit_exceeded"). */
  detail?: string;
  /** Send it again. Absent → no Retry button. */
  onRetry?: () => void;
  retryLabel?: string;
  /** Put the message text on the clipboard. Absent → no Copy button. */
  onCopy?: () => Promise<void> | void;
  /** The one action that clears a `blocked` outcome (e.g. "View reply"). Absent → no button. */
  unblock?: { label: string; onClick: () => void };
  /** Acknowledge and dismiss. Absent → no Done button. */
  onDone?: () => void;
  className?: string;
};

// ============================================================================
// Tone palette
// ============================================================================

type TonePalette = {
  /** Button colour for the primary (Retry) and secondary (unblock / Done) actions. */
  button: ColorName;
  box: string;
  icon: string;
  title: string;
  body: string;
  detail: string;
  Icon: typeof Alerts.AlertTriangle;
};

// Red matches CallFailedNotice, amber matches PendingStepBannerPure, green is the same recipe in green.
const TONE_PALETTE: Readonly<Record<SendOutcomeTone, TonePalette>> = {
  failed: {
    button: 'red',
    box: 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10',
    icon: 'text-red-600 dark:text-red-400',
    title: 'text-red-700 dark:text-red-300',
    body: 'text-red-700/90 dark:text-red-200/80',
    detail: 'border-red-200 bg-white/60 text-red-700/90 dark:border-red-500/20 dark:bg-black/20 dark:text-red-200/80',
    Icon: Alerts.AlertTriangle,
  },
  blocked: {
    button: 'yellow',
    box: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20',
    icon: 'text-amber-600 dark:text-amber-400',
    title: 'text-amber-800 dark:text-amber-200',
    body: 'text-amber-700 dark:text-amber-300',
    detail: 'border-amber-200 bg-white/60 text-amber-700 dark:border-amber-800 dark:bg-black/20 dark:text-amber-300',
    Icon: Alerts.AlertCircle,
  },
  sentRecordPending: {
    button: 'green',
    box: 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10',
    icon: 'text-green-600 dark:text-green-400',
    title: 'text-green-700 dark:text-green-300',
    body: 'text-green-700/90 dark:text-green-200/80',
    detail:
      'border-green-200 bg-white/60 text-green-700/90 dark:border-green-500/20 dark:bg-black/20 dark:text-green-200/80',
    Icon: General.CheckCircle,
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * The outcome of a send the rep just asked for, shown where they asked for it (between the text
 * they wrote and the Send button, or on the queued-step card). Says what happened and what to do in
 * plain words; the provider's code sits behind "More details". Every button appears only when its
 * callback is given, so a surface that cannot retry or copy never shows a dead control.
 *
 * Accessibility: a failed or blocked send is announced at once (`role="alert"`); the green
 * "sent, record pending" one is good news and only needs `role="status"`.
 */
export function SendOutcomeNoticePure({
  tone,
  title,
  body,
  detail,
  onRetry,
  retryLabel = 'Retry',
  onCopy,
  unblock,
  onDone,
  className,
}: SendOutcomeNoticePureProps) {
  const palette = TONE_PALETTE[tone];
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsId = useId();
  const hasDetail = detail != null && detail !== '';
  const hasActions = onRetry != null || unblock != null || onCopy != null || onDone != null || hasDetail;

  return (
    <div
      className={cn('flex w-full flex-col gap-2 rounded-lg border p-3 text-left', palette.box, className)}
      data-test="send-outcome-notice"
      data-tone={tone}>
      {/* The live region is the message only: the buttons below must not re-announce it. */}
      <div role={tone === 'sentRecordPending' ? 'status' : 'alert'} className="flex items-start gap-2">
        <palette.Icon className={cn('mt-0.5 size-4 shrink-0', palette.icon)} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={cn('text-sm font-semibold', palette.title)} data-test="send-outcome-title">
            {title}
          </span>
          {body != null && body !== '' && (
            <p className={cn('text-xs leading-relaxed', palette.body)} data-test="send-outcome-body">
              {body}
            </p>
          )}
        </div>
      </div>

      {hasActions && (
        <div className="flex flex-wrap items-center gap-2 pl-6">
          {onRetry != null && (
            <Button
              size="xs"
              color={palette.button}
              icon={<Arrows.RefreshCw04 />}
              onClick={onRetry}
              data-test="send-outcome-retry">
              {retryLabel}
            </Button>
          )}
          {unblock != null && (
            <Button
              size="xs"
              color={palette.button}
              tone="outline"
              onClick={unblock.onClick}
              data-test="send-outcome-unblock">
              {unblock.label}
            </Button>
          )}
          {onDone != null && (
            <Button size="xs" color={palette.button} tone="outline" onClick={onDone} data-test="send-outcome-done">
              Done
            </Button>
          )}
          {onCopy != null && <CopyFeedbackButton onCopy={onCopy} size="xs" data-test="send-outcome-copy" />}
          {hasDetail && (
            <Button
              size="xs"
              text
              className="ml-auto"
              onClick={() => setDetailsOpen((open) => !open)}
              rIcon={<Arrows.ChevronDown className={cn('transition-transform', detailsOpen && 'rotate-180')} />}
              aria-expanded={detailsOpen}
              aria-controls={detailsId}
              data-test="send-outcome-details-toggle">
              {detailsOpen ? 'Hide details' : 'More details'}
            </Button>
          )}
        </div>
      )}

      {hasDetail && detailsOpen && (
        <div
          id={detailsId}
          className={cn('rounded-md border p-2.5 font-mono text-xs leading-relaxed break-words', palette.detail)}
          data-test="send-outcome-details">
          {detail}
        </div>
      )}
    </div>
  );
}
